import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { YearPlanResponse } from '../api/generated';
import type { EconomicsTreeModel } from '../types/economicsTypes';
import type { EconomicsExportLabels } from '../types/economicsExportTypes';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { selectPatientsById } from '../store/patients/patientsSlice';
import { showError } from '../store/errorDialog/errorDialogSlice';
import { buildExportFileName, exportEconomicsToExcel } from '../services/economicsExportService';

interface UseEconomicsExportArgs {
  yearPlan: YearPlanResponse | null;
  tree: EconomicsTreeModel;
  locationName: string | null;
  year: number;
  fromWeek: number;
  toWeek: number;
}

// Wires the Excel export to the app: translated labels, the patient register (for CPR numbers)
// and error reporting. The row building itself lives in economicsExportService.
export function useEconomicsExport(args: UseEconomicsExportArgs): () => Promise<void> {
  const { yearPlan, tree, locationName, year, fromWeek, toWeek } = args;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const patientsById = useAppSelector(selectPatientsById);

  return useCallback(async () => {
    if (!yearPlan) return;
    const labels: EconomicsExportLabels = {
      sheetName: t('economics-export-sheet'),
      completionClaim: t('economics-completion-label'),
      total: t('economics-export-total'),
      yes: t('economics-export-yes'),
      no: t('economics-export-no'),
      columns: {
        planName: t('economics-export-col-plan'),
        patientName: t('economics-export-col-patient'),
        cprNumber: t('economics-export-col-cpr'),
        moduleName: t('economics-export-col-module'),
        weekNumber: t('economics-export-col-week'),
        startDate: t('economics-export-col-start'),
        endDate: t('economics-export-col-end'),
        amount: t('economics-export-col-amount'),
        isOverridden: t('economics-export-col-overridden'),
        note: t('economics-export-col-note'),
        completedAt: t('economics-export-col-completed-at')
      }
    };
    try {
      await exportEconomicsToExcel(
        { yearPlan, tree, patientsById },
        buildExportFileName(locationName, year, fromWeek, toWeek),
        labels
      );
    } catch {
      dispatch(showError({ message: t('economics-export-error') }));
    }
  }, [yearPlan, tree, patientsById, locationName, year, fromWeek, toWeek, t, dispatch]);
}
